import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  collection, addDoc, updateDoc, doc,
  onSnapshot, query, orderBy, where, serverTimestamp
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { signOut, onAuthStateChanged } from 'firebase/auth';
import { db, storage, auth } from './firebase';
import {
  notifyOnAssignment,
  notifyDefectCancelled,
  notifyOnComment
} from './services/notificationService';
import {
  exportToExcel,
  exportToCSV,
  exportToMarkdown,
  exportToPDF,
  exportToText,
  formatDataForExport
} from './services/exportService';
import { convertDefectToFeature } from './services/conversionService';
import { nextDefectNumber, backfillDefectNumbers } from './services/applicationCounterService';
import { backfillSuiteAppAccess } from './services/suiteAppAccessService';
import { ensureOrgSettings } from './services/orgSettingsService';
import { compressImage, validateImageFile } from './utils/imageCompression';
import { buildPermalink, copyToClipboard, pinEntityToDesktop } from './utils/shortcutHelpers';
import AdminPanel from './AdminPanel';
import ApplicationManager from './ApplicationManager';
import BoardView from './BoardView';
import Notifications from './Notifications';
import DefectTrackerAdminView from './DefectTrackerAdminView';
import './App.css';

function App() {
  const navigate = useNavigate();
  const { defectId } = useParams();  // Get defect ID from URL
  
  // Get user and profile from Suite context via auth
  const [user, setUser]                         = useState(null);
  const [userProfile, setUserProfile]           = useState(null);
  const [authLoading, setAuthLoading]           = useState(true);
  const [defects, setDefects]                   = useState([]);
  const [applications, setApplications]         = useState([]);
  const [users, setUsers]                       = useState([]);
  const [notifications, setNotifications]       = useState([]);
  const [isFormOpen, setIsFormOpen]             = useState(false);
  const [editingDefect, setEditingDefect]       = useState(null);
  const [uploading, setUploading]               = useState(false);
  const [filterStatus, setFilterStatus]         = useState('All');
  const [filterPriority, setFilterPriority]     = useState('All');
  const [filterApplication, setFilterApplication] = useState([]);
  const [sortBy, setSortBy]                     = useState('createdAt');
  const [sortOrder, setSortOrder]               = useState('desc');
  const [expectedImagePreview, setExpectedImagePreview] = useState(null);
  const [actualImagePreview, setActualImagePreview]     = useState(null);
  const [showAdminPanel, setShowAdminPanel]     = useState(false);
  const [showAppManager, setShowAppManager]     = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showAdminView, setShowAdminView]       = useState(false);
  const [commentInputs, setCommentInputs]       = useState({});
  const [editingComment, setEditingComment]     = useState(null);
  const [editCommentText, setEditCommentText]   = useState('');
  const [hiddenLanes, setHiddenLanes]           = useState(new Set());
  const [viewOnlyDefect, setViewOnlyDefect]     = useState(null);

  const [formData, setFormData] = useState({
    applicationName: '', title: '', testStep: '',
    expectedResult: '', expectedResultImage: null, expectedResultImageUrl: '',
    actualResult: '', actualResultImage: null, actualResultImageUrl: '',
    priority: 'Medium', status: 'Open', comments: '', 
    assignedToFix: '', assignedToTest: ''
  });

  const role = userProfile?.role || null;
  const isAdmin   = role === 'Admin';
  const isTester  = role === 'Tester/QA';
  const isCustomer = role === 'Customer';
  const assignedApps = userProfile?.assignedApps || [];

  // ── Auth listener (simplified - Suite handles main auth) ──────────────────
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (currentUser) => {
      setUser(currentUser);
      if (currentUser) {
        const profileUnsub = onSnapshot(
          doc(db, 'users', currentUser.uid),
          (snap) => {
            if (snap.exists()) setUserProfile({ id: snap.id, ...snap.data() });
            else setUserProfile(null);
          }
        );
        setAuthLoading(false);
        return () => profileUnsub();
      } else {
        setUserProfile(null);
        setAuthLoading(false);
      }
    });
    return () => unsub();
  }, []);

  // Set page title based on URL
  useEffect(() => {
    if (defectId) {
      // Permalink - will be updated with actual title when defect loads
      document.title = 'Loading Defect... - Defect Tracker';
    } else {
      document.title = 'Defect Tracker - Wixon & Cross Desk Suite';
    }
  }, [defectId]);

  // Update page title when viewing a specific defect
  useEffect(() => {
    if (viewOnlyDefect) {
      document.title = `${viewOnlyDefect.title} - Defect Tracker`;
    } else if (!defectId) {
      // Only reset to default if not on a permalink URL
      document.title = 'Defect Tracker - Wixon & Cross Desk Suite';
    }
  }, [viewOnlyDefect, defectId]);

  // ── Defects listener (role-filtered + app assignment) ─────────────────────
  useEffect(() => {
    if (!user || !userProfile) return;

    let q;
    if (isCustomer) {
      // Customers see only their own defects for assigned apps
      if (!assignedApps || assignedApps.length === 0) {
        setDefects([]);
        return;
      }
      q = query(
        collection(db, 'defects'),
        where('createdBy', '==', user.uid),
        where('applicationName', 'in', assignedApps.slice(0, 10)), // Firestore limit
        orderBy('createdAt', 'desc')
      );
    } else if (isAdmin || isTester) {
      // Admin/Tester see ALL defects (no app filtering)
      q = query(collection(db, 'defects'), orderBy('createdAt', 'desc'));
    } else {
      // No role assigned
      setDefects([]);
      return;
    }

    const unsub = onSnapshot(q, (snap) => {
      setDefects(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, (err) => {
      console.error('Error fetching defects:', err);
    });

    return () => unsub();
  }, [user, userProfile, isCustomer, isAdmin, isTester, assignedApps]);

  // ── Applications listener ─────────────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, 'applications'), orderBy('name'));
    const unsub = onSnapshot(q, snap => {
      setApplications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  // ── One-time backfill of sequential defect numbers (admin only) ──────────
  // The backfill needs to update defects across all users; only admins have
  // the permissions to do so. Idempotent — safe to run on every admin load.
  useEffect(() => {
    if (!user || !isAdmin) return;
    backfillDefectNumbers().catch(err => {
      console.error('Defect number backfill failed:', err);
    });
    // Build 25: grant existing users access to all active suite apps so
    // the default-DENY rollout is invisible. Also idempotent.
    backfillSuiteAppAccess().catch(err => {
      console.error('Suite app access backfill failed:', err);
    });
    // Build 25.1: create the org settings doc if it doesn't yet exist so
    // every org has a sensible starting state (all apps enabled, default name).
    // Idempotent — does nothing if the doc is already present.
    ensureOrgSettings().catch(err => {
      console.error('ensureOrgSettings failed:', err);
    });
  }, [user, isAdmin]);

  // ── Users listener ─────────────────────────────────────────────────────────
  useEffect(() => {
    const q = query(collection(db, 'users'), orderBy('email'));
    const unsub = onSnapshot(q, snap => {
      setUsers(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, []);

  // ── Notifications listener ─────────────────────────────────────────────────
  useEffect(() => {
    if (!user) return;
    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      where('dismissed', '==', false),
      orderBy('createdAt', 'desc')
    );
    const unsub = onSnapshot(q, snap => {
      setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    });
    return () => unsub();
  }, [user]);

  // Handle permalink - open defect from URL
  useEffect(() => {
    if (defectId && defects.length > 0) {
      const defect = defects.find(d => d.id === defectId);
      if (defect) {
        setViewOnlyDefect(defect);
      }
    }
  }, [defectId, defects]);

  // ── Helpers ────────────────────────────────────────────────────────────────
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleCloseViewOnly = () => {
    setViewOnlyDefect(null);
    // If we're on a permalink URL, navigate back to base defect-tracker
    if (defectId) {
      navigate('/defect-tracker');
    }
  };

  const handleSignOut = async () => {
    try { 
      await signOut(auth);
      navigate('/dashboard');
    } catch (e) { 
      alert('Error signing out.'); 
    }
  };

  const handleImageChange = async (e, fieldType) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate image
    const validation = validateImageFile(file);
    if (!validation.valid) {
      alert(validation.error);
      return;
    }
    
    // Compress image
    const compressedFile = await compressImage(file);
    
    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      if (fieldType === 'expected') {
        setExpectedImagePreview(reader.result);
        setFormData(prev => ({ ...prev, expectedResultImage: compressedFile }));
      } else {
        setActualImagePreview(reader.result);
        setFormData(prev => ({ ...prev, actualResultImage: compressedFile }));
      }
    };
    reader.readAsDataURL(compressedFile);
  };

  const removeImage = (fieldType) => {
    if (fieldType === 'expected') {
      setExpectedImagePreview(null);
      setFormData(prev => ({ ...prev, expectedResultImage: null, expectedResultImageUrl: '' }));
    } else {
      setActualImagePreview(null);
      setFormData(prev => ({ ...prev, actualResultImage: null, actualResultImageUrl: '' }));
    }
  };

  const uploadImage = async (file, defectId, fieldType) => {
    const storageRef = ref(storage, `defects/${defectId}/${fieldType}_${Date.now()}_${file.name}`);
    await uploadBytes(storageRef, file);
    return getDownloadURL(storageRef);
  };

  const createNotification = async ({ userId, type, message, defectId, defectTitle }) => {
    try {
      await addDoc(collection(db, 'notifications'), {
        userId,
        type,
        message,
        defectId: defectId || null,
        defectTitle: defectTitle || null,
        read: false,
        dismissed: false,
        createdAt: serverTimestamp()
      });
    } catch (err) {
      console.error('Error creating notification:', err);
    }
  };

  const resetForm = () => {
    setFormData({
      applicationName: '', title: '', testStep: '',
      expectedResult: '', expectedResultImage: null, expectedResultImageUrl: '',
      actualResult: '', actualResultImage: null, actualResultImageUrl: '',
      priority: 'Medium', status: 'Open', comments: '', 
      assignedToFix: '', assignedToTest: ''
    });
    setEditingDefect(null);
    setIsFormOpen(false);
    setExpectedImagePreview(null);
    setActualImagePreview(null);
  };

  // ── Submit (create / update) ───────────────────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploading(true);
    try {
      const tempId = editingDefect ? editingDefect.id : `temp_${Date.now()}`;
      let expectedImageUrl = formData.expectedResultImageUrl;
      let actualImageUrl   = formData.actualResultImageUrl;

      if (formData.expectedResultImage)
        expectedImageUrl = await uploadImage(formData.expectedResultImage, tempId, 'expected');
      if (formData.actualResultImage)
        actualImageUrl = await uploadImage(formData.actualResultImage, tempId, 'actual');

      const defectData = {
        applicationName: formData.applicationName,
        title: formData.title,
        testStep: formData.testStep,
        expectedResult: formData.expectedResult,
        expectedResultImageUrl: expectedImageUrl || '',
        actualResult: formData.actualResult,
        actualResultImageUrl: actualImageUrl || '',
        priority: formData.priority,
        status: formData.status,
        assignedToFix: formData.assignedToFix || '',
        assignedToTest: formData.assignedToTest || '',
      };

      if (editingDefect) {
        // Check if assignments changed
        const oldFix = editingDefect.assignedToFix;
        const oldTest = editingDefect.assignedToTest;
        const newFix = formData.assignedToFix;
        const newTest = formData.assignedToTest;

        await updateDoc(doc(db, 'defects', editingDefect.id), {
          ...defectData,
          updatedAt: serverTimestamp(),
          updatedBy: user.email
        });

        // Create notifications for new assignments
        if (newFix && newFix !== oldFix) {
          const fixUser = users.find(u => u.email === newFix);
          if (fixUser) {
            await createNotification({
              userId: fixUser.id,
              type: 'assigned_fix',
              message: `You were assigned to fix defect "${formData.title}"`,
              defectId: editingDefect.id,
              defectTitle: formData.title
            });
          }
        }

        if (newTest && newTest !== oldTest) {
          const testUser = users.find(u => u.email === newTest);
          if (testUser) {
            await createNotification({
              userId: testUser.id,
              type: 'assigned_test',
              message: `You were assigned to test defect "${formData.title}"`,
              defectId: editingDefect.id,
              defectTitle: formData.title
            });
          }
        }

        // Notify creator of update
        if (editingDefect.createdBy !== user.uid) {
          await createNotification({
            userId: editingDefect.createdBy,
            type: 'defect_updated',
            message: `Your defect "${formData.title}" was updated`,
            defectId: editingDefect.id,
            defectTitle: formData.title
          });
        }

      } else {
        // Reserve the next sequential number for this application before creating
        // the defect. If reservation fails, we still create the defect (without
        // a displayNumber) — better than blocking the user. The backfill will
        // fix it up later.
        let displayNumber = null;
        try {
          displayNumber = await nextDefectNumber(formData.applicationName);
        } catch (err) {
          console.error('Could not reserve defect number:', err);
        }

        const newDefect = await addDoc(collection(db, 'defects'), {
          ...defectData,
          ...(displayNumber !== null ? { displayNumber } : {}),
          comments: [],
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          createdBy: user.uid,
          createdByEmail: user.email
        });

        // Create notifications for initial assignments
        if (formData.assignedToFix) {
          const fixUser = users.find(u => u.email === formData.assignedToFix);
          if (fixUser) {
            await createNotification({
              userId: fixUser.id,
              type: 'assigned_fix',
              message: `You were assigned to fix defect "${formData.title}"`,
              defectId: newDefect.id,
              defectTitle: formData.title
            });
          }
        }

        if (formData.assignedToTest) {
          const testUser = users.find(u => u.email === formData.assignedToTest);
          if (testUser) {
            await createNotification({
              userId: testUser.id,
              type: 'assigned_test',
              message: `You were assigned to test defect "${formData.title}"`,
              defectId: newDefect.id,
              defectTitle: formData.title
            });
          }
        }
      }
      resetForm();
    } catch (err) {
      console.error('Error saving defect:', err);
      alert('Error saving defect. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // ── Cancel defect (Admin only) ─────────────────────────────────────────────
  const handleCancel = async (defect) => {
    if (!isAdmin) return;
    if (!window.confirm(`Cancel defect "${defect.title}"? This cannot be undone.`)) return;
    try {
      await updateDoc(doc(db, 'defects', defect.id), {
        status: 'Cancelled',
        updatedAt: serverTimestamp(),
        updatedBy: user.email
      });

      // Notify anyone assigned
      [defect.assignedToFix, defect.assignedToTest].forEach(async (assignedEmail) => {
        if (assignedEmail) {
          const assignedUser = users.find(u => u.email === assignedEmail);
          if (assignedUser) {
            await createNotification({
              userId: assignedUser.id,
              type: 'defect_cancelled',
              message: `Defect "${defect.title}" was cancelled`,
              defectId: defect.id,
              defectTitle: defect.title
            });
          }
        }
      });

      // Notify creator
      if (defect.createdBy !== user.uid) {
        await createNotification({
          userId: defect.createdBy,
          type: 'defect_cancelled',
          message: `Your defect "${defect.title}" was cancelled`,
          defectId: defect.id,
          defectTitle: defect.title
        });
      }
    } catch (err) { alert('Error cancelling defect.'); }
  };

  // ── Edit ───────────────────────────────────────────────────────────────────
  const handleEdit = (defect) => {
    setEditingDefect(defect);
    setFormData({
      applicationName: defect.applicationName || '',
      title: defect.title || '',
      testStep: defect.testStep || '',
      expectedResult: defect.expectedResult || '',
      expectedResultImage: null,
      expectedResultImageUrl: defect.expectedResultImageUrl || '',
      actualResult: defect.actualResult || '',
      actualResultImage: null,
      actualResultImageUrl: defect.actualResultImageUrl || '',
      priority: defect.priority || 'Medium',
      status: defect.status || 'Open',
      comments: '',
      assignedToFix: defect.assignedToFix || '',
      assignedToTest: defect.assignedToTest || ''
    });
    setExpectedImagePreview(defect.expectedResultImageUrl || null);
    setActualImagePreview(defect.actualResultImageUrl || null);
    setIsFormOpen(true);
  };

  // ── Add comment ────────────────────────────────────────────────────────────
  const handleAddComment = async (defect, text) => {
    if (!text || !text.trim()) return;
    const existingComments = Array.isArray(defect?.comments) ? defect.comments : [];
    try {
      await updateDoc(doc(db, 'defects', defect.id), {
        comments: [...existingComments, {
          text: text.trim(),
          createdBy: user.email,
          createdAt: new Date().toISOString()
        }],
        updatedAt: serverTimestamp()
      });
      setCommentInputs(prev => ({ ...prev, [defect.id]: '' }));

      // Notify about comment
      await notifyOnComment(defect, users, user.email, user.uid);
    } catch (err) {
      console.error('Error adding comment:', err);
      alert('Error adding comment.');
    }
  };

  // ── Edit comment ───────────────────────────────────────────────────────────
  const handleEditComment = (defectId, commentIndex, comment) => {
    setEditingComment({ defectId, commentIndex });
    setEditCommentText(comment.text);
  };

  const handleSaveCommentEdit = async (defectId, commentIndex) => {
    const text = editCommentText.trim();
    if (!text) return;

    const defect = defects.find(d => d.id === defectId);
    const updatedComments = [...defect.comments];
    updatedComments[commentIndex] = {
      ...updatedComments[commentIndex],
      text,
      editedAt: new Date().toISOString(),
      editedBy: user.email
    };

    try {
      await updateDoc(doc(db, 'defects', defectId), {
        comments: updatedComments,
        updatedAt: serverTimestamp()
      });
      setEditingComment(null);
      setEditCommentText('');
    } catch (err) {
      alert('Error editing comment.');
    }
  };

  const handleCancelCommentEdit = () => {
    setEditingComment(null);
    setEditCommentText('');
  };

  // ── Export functionality ───────────────────────────────────────────────────
  const handleExport = (format) => {
    // Filter out defects in hidden swimlanes
    const visibleDefects = filteredDefects.filter(d => !hiddenLanes.has(d.status));
    const data = formatDataForExport(visibleDefects, 'defect');
    const filename = `defects-${new Date().toISOString().split('T')[0]}`;
    const title = 'Defects Export';
    
    switch (format) {
      case 'excel':
        exportToExcel(data, filename);
        break;
      case 'csv':
        exportToCSV(data, filename);
        break;
      case 'markdown':
        exportToMarkdown(data, filename, title);
        break;
      case 'pdf':
        exportToPDF(data, filename, title);
        break;
      case 'text':
        exportToText(data, filename, title);
        break;
      default:
        break;
    }
  };

  // ── Convert to feature ─────────────────────────────────────────────────────
  const handleConvertToFeature = async (defect) => {
    if (!window.confirm(`Convert "${defect.title}" to a Feature Request? This will delete the defect.`)) {
      return;
    }
    
    try {
      await convertDefectToFeature(defect);
      alert('Successfully converted to Feature Request!');
    } catch (error) {
      console.error('Conversion error:', error);
      alert('Error converting defect: ' + error.message);
    }
  };

  // ── Status options per role ────────────────────────────────────────────────
  const getStatusOptions = () => {
    if (isAdmin) return ['Open','In Progress','Resolved','Closed','Reopen','Recommend Cancel','Cancelled'];
    if (isTester) return ['Open','In Progress','Resolved','Closed','Reopen','Recommend Cancel'];
    return ['Open'];
  };

  // ── Visual helpers ─────────────────────────────────────────────────────────
  const getPriorityColor = (p) => ({
    Critical: '#dc2626', High: '#ea580c', Medium: '#ca8a04', Low: '#16a34a'
  }[p] || '#6b7280');

  const getStatusColor = (s) => ({
    Open: '#2563eb', 'In Progress': '#7c3aed', Resolved: '#16a34a',
    Closed: '#6b7280', Reopen: '#dc2626', Cancelled: '#374151',
    'Recommend Cancel': '#f59e0b'
  }[s] || '#6b7280');

  const filteredDefects = defects.filter(d => {
    const sMatch = filterStatus === 'All' || d.status === filterStatus;
    const pMatch = filterPriority === 'All' || d.priority === filterPriority;
    const aMatch = filterApplication.length === 0 || filterApplication.includes(d.applicationName);
    return sMatch && pMatch && aMatch;
  }).sort((a, b) => {
    let aVal, bVal;
    
    if (sortBy === 'createdAt' || sortBy === 'updatedAt') {
      aVal = a[sortBy]?.toDate?.() || new Date(0);
      bVal = b[sortBy]?.toDate?.() || new Date(0);
    } else if (sortBy === 'priority') {
      const priorityOrder = { 'Critical': 4, 'High': 3, 'Medium': 2, 'Low': 1 };
      aVal = priorityOrder[a.priority] || 0;
      bVal = priorityOrder[b.priority] || 0;
    } else if (sortBy === 'applicationName') {
      aVal = (a.applicationName || '').toLowerCase();
      bVal = (b.applicationName || '').toLowerCase();
    } else if (sortBy === 'displayNumber') {
      // Items without a number sort to the end (treat as Infinity)
      aVal = typeof a.displayNumber === 'number' ? a.displayNumber : Number.POSITIVE_INFINITY;
      bVal = typeof b.displayNumber === 'number' ? b.displayNumber : Number.POSITIVE_INFINITY;
    } else {
      aVal = a[sortBy] || '';
      bVal = b[sortBy] || '';
    }
    
    if (sortOrder === 'asc') {
      return aVal > bVal ? 1 : aVal < bVal ? -1 : 0;
    } else {
      return aVal < bVal ? 1 : aVal > bVal ? -1 : 0;
    }
  });

  // ── Guards ─────────────────────────────────────────────────────────────────
  if (authLoading) return (
    <div className="App">
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading...</p>
      </div>
    </div>
  );

  if (!user || !userProfile) return (
    <div className="App">
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Setting up...</p>
      </div>
    </div>
  );

  if (showAdminView && isAdmin) return (
    <DefectTrackerAdminView 
      onNavigate={(section) => {
        if (section === 'users') {
          setShowAdminView(false);
          setShowAdminPanel(true);
        } else if (section === 'apps') {
          setShowAdminView(false);
          setShowAppManager(true);
        }
      }}
      onBack={() => setShowAdminView(false)}
    />
  );

  if (showAdminPanel && isAdmin) return (
    <AdminPanel user={user} onBack={() => setShowAdminPanel(false)} />
  );

  if (showAppManager && isAdmin) return (
    <ApplicationManager user={user} onBack={() => setShowAppManager(false)} />
  );

  if (showNotifications) return (
    <Notifications user={user} onBack={() => setShowNotifications(false)} />
  );

  const unreadCount = notifications.filter(n => !n.read).length;

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <div className="App">
      <header className="header">
        <div className="header-content">
          <div>
            <div className="breadcrumb-nav-app">
              <button className="breadcrumb-item" onClick={() => navigate('/desktop')}>
                🖥️ Desktop
              </button>
              <span className="breadcrumb-separator">›</span>
              <button className="breadcrumb-item" onClick={() => navigate('/dashboard')}>
                📦 Dashboard
              </button>
              <span className="breadcrumb-separator">›</span>
              <span className="breadcrumb-item breadcrumb-current">🐛 Defect Tracker</span>
            </div>
          </div>
          <div className="header-user">
            <button 
              className="btn btn-notification" 
              onClick={() => setShowNotifications(true)}
            >
              🔔 Notifications
              {unreadCount > 0 && <span className="notification-badge">{unreadCount}</span>}
            </button>
            <div className="user-info-section">
              <span className="user-email">{user.email}</span>
              <span className="user-role-label">Role: <span className="role-badge role-badge--header">{role}</span></span>
            </div>
            {isAdmin && (
              <button className="btn btn-warning btn-small" onClick={() => navigate('/admin')}>
                ⚙️ Admin
              </button>
            )}
            <button className="btn btn-secondary btn-small" onClick={handleSignOut}>
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="main-content">
        {/* Stats */}
        <div className="stats">
          {[
            { label: 'Total', count: filteredDefects.length },
            { label: 'Open', count: filteredDefects.filter(d => d.status === 'Open').length },
            { label: 'In Progress', count: filteredDefects.filter(d => d.status === 'In Progress').length },
            { label: 'Resolved', count: filteredDefects.filter(d => d.status === 'Resolved').length },
            { label: 'Recommend Cancel', count: filteredDefects.filter(d => d.status === 'Recommend Cancel').length },
          ].map(s => (
            <div key={s.label} className={`stat-card${s.label === 'Recommend Cancel' && s.count > 0 ? ' stat-card--alert' : ''}`}>
              <div className="stat-value">{s.count}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Toolbar */}
        <div className="toolbar">
          <button className="btn btn-primary" onClick={() => setIsFormOpen(true)}>
            + New Defect
          </button>
          <div className="export-dropdown">
            <button 
              className="btn btn-secondary"
              onClick={() => setShowExportDropdown(!showExportDropdown)}
            >
              📊 Export ▼
            </button>
            {showExportDropdown && (
              <div className="dropdown-menu">
                <button onClick={() => { handleExport('excel'); setShowExportDropdown(false); }}>Excel (.xlsx)</button>
                <button onClick={() => { handleExport('csv'); setShowExportDropdown(false); }}>CSV (.csv)</button>
                <button onClick={() => { handleExport('markdown'); setShowExportDropdown(false); }}>Markdown (.md)</button>
                <button onClick={() => { handleExport('pdf'); setShowExportDropdown(false); }}>PDF (.pdf)</button>
                <button onClick={() => { handleExport('text'); setShowExportDropdown(false); }}>Text (.txt)</button>
              </div>
            )}
          </div>
        </div>

        {/* Filters */}
        <div className="toolbar">
          <div className="filters">
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)} className="filter-select">
              <option value="All">All Status</option>
              {['Open','In Progress','Resolved','Closed','Reopen','Recommend Cancel','Cancelled'].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select value={filterPriority} onChange={e => setFilterPriority(e.target.value)} className="filter-select">
              <option value="All">All Priorities</option>
              {['Critical','High','Medium','Low'].map(p => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
            <div className="filter-multiselect">
              <label>Applications:</label>
              <div className="multiselect-dropdown">
                <button 
                  className="multiselect-button"
                  onClick={() => {
                    const dropdown = document.getElementById('app-filter-dropdown');
                    dropdown.style.display = dropdown.style.display === 'block' ? 'none' : 'block';
                  }}
                >
                  {filterApplication.length === 0 
                    ? 'All Applications' 
                    : `${filterApplication.length} selected`}
                  <span className="dropdown-arrow">▼</span>
                </button>
                <div id="app-filter-dropdown" className="multiselect-dropdown-content" style={{display: 'none'}}>
                  <div className="multiselect-actions">
                    <button 
                      className="btn-link" 
                      onClick={() => setFilterApplication(applications.map(a => a.name))}
                    >
                      Select All
                    </button>
                    <button 
                      className="btn-link" 
                      onClick={() => setFilterApplication([])}
                    >
                      Clear All
                    </button>
                  </div>
                  <div className="multiselect-options">
                    {applications.map(app => (
                      <label key={app.id} className="checkbox-label">
                        <input
                          type="checkbox"
                          checked={filterApplication.includes(app.name)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFilterApplication([...filterApplication, app.name]);
                            } else {
                              setFilterApplication(filterApplication.filter(a => a !== app.name));
                            }
                          }}
                        />
                        <span>{app.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sort Controls */}
          <div className="sort-controls">
            <label>Sort by:</label>
            <select value={sortBy} onChange={e => setSortBy(e.target.value)} className="filter-select">
              <option value="displayNumber">Number (e.g. WP-D.1)</option>
              <option value="createdAt">Created Date</option>
              <option value="updatedAt">Updated Date</option>
              <option value="priority">Priority</option>
              <option value="applicationName">Application</option>
              <option value="title">Title</option>
            </select>
            <select value={sortOrder} onChange={e => setSortOrder(e.target.value)} className="filter-select">
              <option value="desc">Descending</option>
              <option value="asc">Ascending</option>
            </select>
          </div>
        </div>

        {/* Form Modal */}
        {isFormOpen && (
          <div className="modal-overlay" onClick={resetForm}>
            <div className="modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>{editingDefect ? 'Edit Defect' : 'New Defect'}</h3>
                <button className="close-btn" onClick={resetForm}>×</button>
              </div>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Application Name *</label>
                  <select
                    name="applicationName"
                    value={formData.applicationName}
                    onChange={handleInputChange}
                    required
                  >
                    <option value="">-- Select Application --</option>
                    {applications
                      .filter(app => isAdmin || isTester || assignedApps.includes(app.name))
                      .map(app => (
                        <option key={app.id} value={app.name}>{app.name}</option>
                      ))}
                  </select>
                  {isCustomer && assignedApps.length === 0 && (
                    <p className="field-note">
                      No applications assigned. Please contact an administrator.
                    </p>
                  )}
                  {isAdmin && applications.length === 0 && (
                    <p className="field-note">
                      No applications defined. Click "📱 Manage Apps" to add one.
                    </p>
                  )}
                </div>

                <div className="form-group">
                  <label>Title *</label>
                  <input type="text" name="title" value={formData.title}
                    onChange={handleInputChange} required placeholder="Brief description of the defect" />
                </div>

                <div className="form-group">
                  <label>Test Step/Summary *</label>
                  <textarea name="testStep" value={formData.testStep} onChange={handleInputChange}
                    required rows="3" placeholder="Steps to reproduce or summary of the issue" />
                </div>

                <div className="form-row">
                  {/* Expected Result */}
                  <div className="form-group">
                    <label>Expected Result *</label>
                    <textarea name="expectedResult" value={formData.expectedResult}
                      onChange={handleInputChange} required rows="3" placeholder="What should happen" />
                    <div className="image-upload-section">
                      <label className="image-upload-label">
                        <input type="file" accept="image/*"
                          onChange={e => handleImageChange(e, 'expected')} style={{ display: 'none' }} />
                        <span className="upload-btn">📎 Attach Screenshot</span>
                      </label>
                      {expectedImagePreview && (
                        <div className="image-preview">
                          <img src={expectedImagePreview} alt="Expected result" />
                          <button type="button" className="remove-image-btn" onClick={() => removeImage('expected')}>✕</button>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Actual Result */}
                  <div className="form-group">
                    <label>Actual Result *</label>
                    <textarea name="actualResult" value={formData.actualResult}
                      onChange={handleInputChange} required rows="3" placeholder="What actually happened" />
                    <div className="image-upload-section">
                      <label className="image-upload-label">
                        <input type="file" accept="image/*"
                          onChange={e => handleImageChange(e, 'actual')} style={{ display: 'none' }} />
                        <span className="upload-btn">📎 Attach Screenshot</span>
                      </label>
                      {actualImagePreview && (
                        <div className="image-preview">
                          <img src={actualImagePreview} alt="Actual result" />
                          <button type="button" className="remove-image-btn" onClick={() => removeImage('actual')}>✕</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Priority *</label>
                    <select name="priority" value={formData.priority} onChange={handleInputChange} required>
                      {['Low','Medium','High','Critical'].map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                  </div>

                  {/* Status - hidden for new Customer submissions */}
                  {(isAdmin || isTester || editingDefect) && (
                    <div className="form-group">
                      <label>Status *</label>
                      <select name="status" value={formData.status} onChange={handleInputChange} required>
                        {getStatusOptions().map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </div>
                  )}
                </div>

                {/* Fix and Test Assignments - Admin only */}
                {isAdmin && (
                  <div className="form-row">
                    <div className="form-group">
                      <label>Assign to Fix</label>
                      <select
                        name="assignedToFix"
                        value={formData.assignedToFix}
                        onChange={handleInputChange}
                      >
                        <option value="">-- Select User --</option>
                        {users.filter(u => u.role !== 'Customer').map(u => (
                          <option key={u.id} value={u.email}>{u.email}</option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Assign to Test</label>
                      <select
                        name="assignedToTest"
                        value={formData.assignedToTest}
                        onChange={handleInputChange}
                      >
                        <option value="">-- Select User --</option>
                        {users.filter(u => u.role !== 'Customer').map(u => (
                          <option key={u.id} value={u.email}>{u.email}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                <div className="form-actions">
                  <button type="button" className="btn btn-secondary" onClick={resetForm}>Cancel</button>
                  <button type="submit" className="btn btn-primary" disabled={uploading}>
                    {uploading ? 'Uploading...' : (editingDefect ? 'Update Defect' : 'Create Defect')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View-Only Defect Modal */}
        {viewOnlyDefect && (
          <div className="modal-overlay" onClick={handleCloseViewOnly}>
            <div className="modal view-only-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h3>View Defect</h3>
                <div style={{display: 'flex', gap: '0.5rem', alignItems: 'center'}}>
                  <button 
                    className="btn btn-secondary btn-small"
                    onClick={async () => {
                      const url = buildPermalink('/defect-tracker', viewOnlyDefect.id);
                      const ok = await copyToClipboard(url);
                      alert(ok ? 'Permalink copied to clipboard!' : 'Could not copy link');
                    }}
                    title="Copy link to this defect"
                  >
                    🔗 Copy Link
                  </button>
                  <button
                    className="btn btn-secondary btn-small"
                    onClick={async () => {
                      try {
                        await pinEntityToDesktop({
                          userId: user.uid,
                          type: 'defect',
                          entityId: viewOnlyDefect.id,
                          title: viewOnlyDefect.title,
                          icon: '🐛'
                        });
                        alert('Pinned to desktop');
                      } catch (err) {
                        alert(`Could not pin: ${err?.message || 'unknown error'}`);
                      }
                    }}
                    title="Pin this defect to your desktop"
                  >
                    📎 Pin to Desktop
                  </button>
                  <span className="view-only-badge">👁️ View Only</span>
                  <button className="close-btn" onClick={handleCloseViewOnly}>×</button>
                </div>
              </div>
              <div className="modal-body">
                <div className="form-group">
                  <label>Application Name</label>
                  <input type="text" value={viewOnlyDefect.applicationName} disabled />
                </div>
                <div className="form-group">
                  <label>Title</label>
                  <input type="text" value={viewOnlyDefect.title} disabled />
                </div>
                <div className="form-group">
                  <label>Test Step/Summary</label>
                  <textarea value={viewOnlyDefect.testStep || ''} disabled rows="3" />
                </div>
                <div className="form-group">
                  <label>Expected Result</label>
                  <textarea value={viewOnlyDefect.expectedResult || ''} disabled rows="2" />
                </div>
                {viewOnlyDefect.expectedResultImageUrl && (
                  <div className="form-group">
                    <label>Expected Result Image</label>
                    <img src={viewOnlyDefect.expectedResultImageUrl} alt="Expected" style={{maxWidth: '100%', borderRadius: '4px'}} />
                  </div>
                )}
                <div className="form-group">
                  <label>Actual Result</label>
                  <textarea value={viewOnlyDefect.actualResult || ''} disabled rows="2" />
                </div>
                {viewOnlyDefect.actualResultImageUrl && (
                  <div className="form-group">
                    <label>Actual Result Image</label>
                    <img src={viewOnlyDefect.actualResultImageUrl} alt="Actual" style={{maxWidth: '100%', borderRadius: '4px'}} />
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label>Priority</label>
                    <input type="text" value={viewOnlyDefect.priority} disabled />
                  </div>
                  <div className="form-group">
                    <label>Status</label>
                    <input type="text" value={viewOnlyDefect.status} disabled />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Assigned to Fix</label>
                    <input type="text" value={viewOnlyDefect.assignedToFix || 'Unassigned'} disabled />
                  </div>
                  <div className="form-group">
                    <label>Assigned to Test</label>
                    <input type="text" value={viewOnlyDefect.assignedToTest || 'Unassigned'} disabled />
                  </div>
                </div>
                <div className="form-group">
                  <label>Comments ({viewOnlyDefect.comments?.length || 0})</label>
                  <div className="comments-view-only">
                    {viewOnlyDefect.comments?.map((comment, idx) => {
                      // Handle different date formats safely
                      let dateStr = 'Unknown date';
                      if (comment.createdAt) {
                        try {
                          if (typeof comment.createdAt.toDate === 'function') {
                            // Firestore Timestamp
                            dateStr = comment.createdAt.toDate().toLocaleString();
                          } else if (comment.createdAt instanceof Date) {
                            // JavaScript Date
                            dateStr = comment.createdAt.toLocaleString();
                          } else if (typeof comment.createdAt === 'string') {
                            // String date
                            dateStr = new Date(comment.createdAt).toLocaleString();
                          }
                        } catch (e) {
                          console.warn('Error formatting comment date:', e);
                        }
                      }
                      
                      return (
                        <div key={idx} className="comment-item-readonly">
                          <strong>{comment.email}</strong>
                          <span className="comment-date">{dateStr}</span>
                          <p>{comment.text}</p>
                        </div>
                      );
                    })}
                    {(!viewOnlyDefect.comments || viewOnlyDefect.comments.length === 0) && (
                      <p className="no-comments">No comments yet</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Board View */}
        <BoardView
          defects={filteredDefects}
          user={user}
          userRole={role}
          users={users}
          onEditDefect={handleEdit}
          onViewDefect={setViewOnlyDefect}
          onCancelDefect={handleCancel}
          onAddComment={handleAddComment}
          commentInputs={commentInputs}
          setCommentInputs={setCommentInputs}
          onCreateNotification={createNotification}
          editingComment={editingComment}
          editCommentText={editCommentText}
          setEditCommentText={setEditCommentText}
          onEditComment={handleEditComment}
          onSaveCommentEdit={handleSaveCommentEdit}
          onCancelCommentEdit={handleCancelCommentEdit}
          onConvertToFeature={handleConvertToFeature}
          onHiddenLanesChange={setHiddenLanes}
        />
      </main>
      
      <footer className="footer">
        <p>&copy; 2026 Wixon & Cross LLC. All rights reserved.</p>
      </footer>
    </div>
  );
}

export default App;
